import { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export interface ChatMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  created_at: string;
  sender?: {
    id: string;
    full_name: string;
  };
}

export interface ChatConversation {
  id: string;
  name: string | null;
  type: 'direct' | 'group';
  created_at: string;
  updated_at: string;
  last_message?: ChatMessage;
  unread_count?: number;
  participants?: Array<{
    user_id: string;
    user?: {
      id: string;
      full_name: string;
    };
  }>;
}

export function useStaffChat() {
  const { user, userRole } = useAuth();
  const queryClient = useQueryClient();
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);

  const canSendMessages = ['superadmin', 'admin', 'supervisor', 'plotter'].includes(userRole || '');

  const { data: conversations = [], isLoading: loadingConversations } = useQuery({
    queryKey: ['staff-chat-conversations', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data: participantData } = await api
        .from('chat_participants')
        .select('conversation_id')
        .eq('user_id', user.id);

      if (!participantData?.length) return [];

      const conversationIds = participantData.map(p => p.conversation_id);

      const { data: convData } = await api
        .from('chat_conversations')
        .select('*')
        .in('id', conversationIds)
        .order('updated_at', { ascending: false });

      if (!convData) return [];

      // Fetch participants separately to avoid FK issues
      const { data: participantsData } = await api
        .from('chat_participants')
        .select('conversation_id, user_id')
        .in('conversation_id', conversationIds);

      // Fetch profiles for participants
      const userIds = [...new Set(participantsData?.map(p => p.user_id) || [])];
      const { data: profilesData } = await api
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);

      const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);

      // Combine data
      return convData.map(conv => ({
        ...conv,
        participants: participantsData
          ?.filter(p => p.conversation_id === conv.id)
          .map(p => ({
            user_id: p.user_id,
            user: profilesMap.get(p.user_id),
          })) || [],
      })) as ChatConversation[];
    },
    enabled: !!user?.id,
    refetchInterval: 30000,
  });

  const { data: messages = [], isLoading: loadingMessages } = useQuery({
    queryKey: ['staff-chat-messages', activeConversationId],
    queryFn: async () => {
      if (!activeConversationId) return [];

      const { data: messagesData } = await api
        .from('chat_messages')
        .select('*')
        .eq('conversation_id', activeConversationId)
        .order('created_at', { ascending: true })
        .limit(100);

      if (!messagesData) return [];

      // Fetch senders
      const senderIds = [...new Set(messagesData.map(m => m.sender_id))];
      const { data: sendersData } = await api
        .from('profiles')
        .select('id, full_name')
        .in('id', senderIds);

      const sendersMap = new Map(sendersData?.map(s => [s.id, s]) || []);

      return messagesData.map(m => ({
        ...m,
        sender: sendersMap.get(m.sender_id),
      })) as ChatMessage[];
    },
    enabled: !!activeConversationId,
    refetchInterval: 5000,
  });

  const { data: availableUsers = [] } = useQuery({
    queryKey: ['staff-chat-users'],
    queryFn: async () => {
      const { data } = await api
        .from('profiles')
        .select('id, full_name, employee_id')
        .neq('id', user?.id || '')
        .order('full_name');

      return data || [];
    },
    enabled: !!user?.id,
  });

  const sendMessageMutation = useMutation({
    mutationFn: async ({ conversationId, content }: { conversationId: string; content: string }) => {
      const { data, error } = await api
        .from('chat_messages')
        .insert({
          conversation_id: conversationId,
          sender_id: user?.id,
          content,
        })
        .select()
        .single();

      if (error) throw error;

      await api
        .from('chat_conversations')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', conversationId);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-chat-messages', activeConversationId] });
      queryClient.invalidateQueries({ queryKey: ['staff-chat-conversations'] });
    },
    onError: (error) => {
      console.error('Error sending message:', error);
      toast.error('Error al enviar mensaje');
    },
  });

  const startConversationMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      // Check if conversation already exists
      const { data: existingParticipants } = await api
        .from('chat_participants')
        .select('conversation_id')
        .eq('user_id', user?.id);

      if (existingParticipants?.length) {
        const convIds = existingParticipants.map(p => p.conversation_id);

        const { data: targetParticipants } = await api
          .from('chat_participants')
          .select('conversation_id')
          .eq('user_id', targetUserId)
          .in('conversation_id', convIds);

        if (targetParticipants?.length) {
          // Check if it's a direct conversation
          const { data: conv } = await api
            .from('chat_conversations')
            .select('id, type')
            .eq('id', targetParticipants[0].conversation_id)
            .eq('type', 'direct')
            .single();

          if (conv) {
            return conv.id;
          }
        }
      }

      // Create new conversation
      const { data: newConv, error: convError } = await api
        .from('chat_conversations')
        .insert({
          type: 'direct',
          created_by: user?.id,
        })
        .select()
        .single();

      if (convError) throw convError;

      // Add participants
      const { error: partError } = await api.from('chat_participants').insert([
        { conversation_id: newConv.id, user_id: user?.id },
        { conversation_id: newConv.id, user_id: targetUserId },
      ]);

      if (partError) throw partError;

      return newConv.id;
    },
    onSuccess: (conversationId) => {
      setActiveConversationId(conversationId);
      queryClient.invalidateQueries({ queryKey: ['staff-chat-conversations'] });
    },
    onError: (error) => {
      console.error('Error creating conversation:', error);
      toast.error('Error al crear conversacion');
    },
  });

  const sendMessage = useCallback(async (content: string) => {
    if (!activeConversationId || !content.trim()) return;
    await sendMessageMutation.mutateAsync({ conversationId: activeConversationId, content });
  }, [activeConversationId, sendMessageMutation]);

  const startConversation = useCallback(async (targetUserId: string) => {
    await startConversationMutation.mutateAsync(targetUserId);
  }, [startConversationMutation]);

  const totalUnread = conversations.reduce((acc, conv) => acc + (conv.unread_count || 0), 0);

  return {
    conversations,
    messages,
    availableUsers,
    activeConversationId,
    setActiveConversationId,
    sendMessage,
    startConversation,
    loadingConversations,
    loadingMessages,
    isSending: sendMessageMutation.isPending,
    totalUnread,
    currentUserId: user?.id,
    canSendMessages,
  };
}
